// ProductoImagen - Sprint 44 Fase 3 Vista 3.
// Panel de imagen del producto (upload/cambiar/eliminar + preview).
// Integra con los handlers del hook useProductoFormXL (onUploadImage,
// onDeleteImage). Si productoId es null (creacion) se muestra el mensaje
// "Guarda el producto primero..." porque el backend requiere el id en la
// URL del endpoint POST /:id/imagen.
import { useEffect, useRef } from 'react';
import { ensureKeyframes, injectCSS } from '../../../design/motion';
import { mediaUrl } from '../utils';

const CSS_ID = 'xl-prod-imagen-css';
const CSS = `
.xl-prod-img {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  background: var(--xl-color-surface-alt);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-md);
  padding: 14px;
  font-family: var(--xl-font-sans);
}
.xl-prod-img__box {
  width: 128px; height: 128px;
  background: var(--xl-color-bg);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-md);
  display: flex; align-items: center; justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}
.xl-prod-img__box img {
  max-width: 100%; max-height: 100%;
  object-fit: contain;
}
.xl-prod-img__placeholder {
  font-size: 11px;
  color: var(--xl-color-text-muted);
  text-align: center;
  padding: 0 8px;
  line-height: 1.3;
}
.xl-prod-img__body {
  flex: 1; min-width: 0;
  display: flex; flex-direction: column;
  gap: 6px;
}
.xl-prod-img__title {
  font-size: 13px; font-weight: 600;
  color: var(--xl-color-text);
}
.xl-prod-img__hint {
  font-size: 11px;
  color: var(--xl-color-text-muted);
  line-height: 1.4;
}
.xl-prod-img__actions {
  display: flex; gap: 8px; flex-wrap: wrap;
  margin-top: 4px;
}
.xl-prod-img__btn {
  font: 500 12px/1 var(--xl-font-sans);
  padding: 8px 12px;
  border-radius: var(--xl-radius-md);
  border: 1px solid transparent;
  cursor: pointer;
  transition: background 120ms cubic-bezier(0,0,0.2,1),
              border-color 120ms cubic-bezier(0,0,0.2,1);
}
.xl-prod-img__btn:focus-visible { outline: 2px solid var(--xl-color-focus); outline-offset: 2px }
.xl-prod-img__btn--primary {
  background: var(--xl-color-primary);
  color: var(--xl-color-text-inverse);
}
.xl-prod-img__btn--primary:hover { background: var(--xl-color-primary-hover) }
.xl-prod-img__btn--danger {
  background: transparent;
  color: var(--xl-color-danger);
  border-color: rgba(220,38,38,0.35);
}
.xl-prod-img__btn--danger:hover { background: rgba(220,38,38,0.08) }
.xl-prod-img__btn:disabled { opacity: 0.45; cursor: not-allowed }
.xl-prod-img__note {
  font-size: 11px; font-style: italic;
  color: var(--xl-color-text-muted);
}
`;

export default function ProductoImagen({
  productoId,
  imagenUrl,
  imagenThumbnailUrl,
  uploading = false,
  onUpload,
  onDelete,
}) {
  useEffect(() => { ensureKeyframes(); injectCSS(CSS_ID, CSS); }, []);
  const fileInputRef = useRef(null);

  const triggerFilePick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleDelete = () => {
    if (typeof onDelete !== 'function') return;
    if (!window.confirm('Eliminar imagen del producto?')) return;
    onDelete();
  };

  const thumb = imagenThumbnailUrl || imagenUrl;

  return (
    <div className="xl-prod-img">
      <div className="xl-prod-img__box" aria-hidden="true">
        {thumb ? (
          <img src={mediaUrl(thumb)} alt="" />
        ) : (
          <span className="xl-prod-img__placeholder">Sin imagen</span>
        )}
      </div>
      <div className="xl-prod-img__body">
        <div className="xl-prod-img__title">Imagen del producto</div>
        <div className="xl-prod-img__hint">
          JPG, PNG o WEBP. Maximo 2 MB. Se genera miniatura automaticamente.
        </div>
        {!productoId ? (
          <div className="xl-prod-img__note">
            Guarda el producto primero para poder subir imagen.
          </div>
        ) : (
          <div className="xl-prod-img__actions">
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.webp"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files && e.target.files[0];
                if (f && typeof onUpload === 'function') onUpload(f);
                e.target.value = '';
              }}
            />
            <button
              type="button"
              className="xl-prod-img__btn xl-prod-img__btn--primary"
              onClick={triggerFilePick}
              disabled={uploading}
            >
              {uploading
                ? 'Subiendo\u2026'
                : (imagenUrl ? 'Cambiar imagen' : 'Subir imagen')}
            </button>
            {imagenUrl && (
              <button
                type="button"
                className="xl-prod-img__btn xl-prod-img__btn--danger"
                onClick={handleDelete}
                disabled={uploading}
              >
                Eliminar
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
